import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { AuthContext } from '../App';
import { useActivePlan } from '../lib/useActivePlan';

/**
 * Reusable banner showing current plan, benefits, and remaining usage.
 * Drop into any feature page to show package status.
 */
export function PackageBanner() {
  const { user } = useContext(AuthContext);
  const { planLabel, isPaid, expiresAt, usageRemaining, benefits, planBadge } = useActivePlan(user);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : null;

  if (!isPaid) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6" data-testid="package-banner-free">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">Free Plan</p>
            <p className="text-xs text-gray-500">Upgrade for full access to this feature</p>
          </div>
          <Link to="/pricing"><Button size="sm" className="btn-uae-red">Upgrade</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-4 mb-6 text-white" data-testid="package-banner-paid">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-yellow-400" />
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${planBadge}`}>{planLabel}</span>
          {expiresAt && (
            <span className="text-xs opacity-70 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Valid until {fmtDate(expiresAt)}
            </span>
          )}
        </div>
      </div>
      {Object.keys(usageRemaining).length > 0 ? (
        <div className="flex flex-wrap gap-3">
          {benefits.filter(b => usageRemaining[b.key] !== undefined).map(b => (
            <div key={b.key} className="bg-white bg-opacity-10 rounded px-3 py-1.5">
              <span className="text-xs opacity-80">{b.label}: </span>
              <span className="text-sm font-bold">{usageRemaining[b.key]} left</span>
            </div>
          ))}
          {benefits.filter(b => usageRemaining[b.key] === undefined).map(b => (
            <div key={b.key} className="bg-white bg-opacity-10 rounded px-3 py-1.5">
              <span className="text-xs opacity-80">{b.label}: </span>
              <span className="text-xs text-green-300 font-medium">Included</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {benefits.map(b => (
            <div key={b.key} className="bg-white bg-opacity-10 rounded px-3 py-1.5">
              <span className="text-xs opacity-80">{b.label}: </span>
              <span className="text-xs text-green-300 font-medium">Included</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
